import { useAtomValue } from "jotai";
import {
    ConversationStateStatus,
    CurrentConversationState,
} from "./conversation-atom.reducer";
import { useCurrentConversationContext } from "./current-conversation-provider";
import { useEffect, useMemo } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { MessageRole } from "models/enumerations/conversations/message-role";
import StringUtils from "utilities/string-utils";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import usePrevious from "utilities/hooks/use-previous";
import ConversationRecord from "models/view-models/conversations/conversation-record";

export interface UseConversationHookOptions {
    onConversationLoaded?: (conversation: ConversationRecord) => void;
}

/**
 * Custom hook for reading the state of the current conversation.
 */
export function useConversationState({
    onConversationLoaded,
}: UseConversationHookOptions = {}): CurrentConversationState {
    const atom = useCurrentConversationContext();
    const state = useAtomValue(atom);
    const { currentConversation, status } = state;

    const conversation = useMemo(() => {
        if (currentConversation == null) {
            return undefined;
        }

        if (CollectionUtils.isEmpty(currentConversation.messages)) {
            return currentConversation;
        }

        const { role, content, toolCalls, ...message } =
            currentConversation.messages[
                currentConversation.messages.length - 1
            ];

        if (hasDisplayableAssistantOutput(role, content, toolCalls)) {
            return currentConversation;
        }

        const parentId =
            message.parentId ??
            currentConversation.messages
                .filter((m) => m.role === MessageRole.User)
                .at(-1)?.id;

        const trailingMessage = new ConversationMessageRecord({
            parentId,
            content:
                status === ConversationStateStatus.StreamingMessage ||
                status === ConversationStateStatus.ConversationLoading
                    ? "Thinking..."
                    : status ===
                            ConversationStateStatus.ConversationMaxLengthReached ||
                        status === ConversationStateStatus.ConversationReady
                      ? ""
                      : "This message was cancelled or there was an error in the response. Would you like to retry?",
            role: MessageRole.Assistant,
            status:
                status === ConversationStateStatus.StreamingMessage ||
                status === ConversationStateStatus.ConversationLoading
                    ? ConversationMessageStatus.Thinking
                    : status ===
                        ConversationStateStatus.ConversationMaxLengthReached
                      ? ConversationMessageStatus.MaxLengthReached
                      : ConversationMessageStatus.Cancelled,
        });

        return currentConversation.with({
            messages: [...currentConversation.messages, trailingMessage],
        });
    }, [currentConversation, status]);

    const previousStatus = usePrevious(status);

    useEffect(() => {
        if (
            currentConversation != null &&
            previousStatus === ConversationStateStatus.ConversationLoading &&
            status === ConversationStateStatus.ConversationReady
        ) {
            onConversationLoaded?.(currentConversation);
        }
    }, [currentConversation, previousStatus, status, onConversationLoaded]);

    return {
        ...state,
        currentConversation: conversation,
    };
}

export function hasDisplayableAssistantOutput(
    role: MessageRole | undefined,
    content: string | undefined,
    toolCalls: ConversationMessageRecord["toolCalls"]
): boolean {
    return (
        role === MessageRole.Assistant &&
        (!StringUtils.isEmpty(content) ||
            toolCalls?.some((toolCall) => toolCall.shouldDisplay()) === true)
    );
}
